import { useState, useCallback, useRef } from 'react';

export interface LotteryEntry {
  id: number;
  number: string;
  weight: number;
}

export interface HistoryItem {
  number: string;
  time: Date;
}

export interface LangData {
  label: string;
  htmlLang: string;
  locale: string;
  title: string;
  subtitle: string;
  desc: string;
  sectionLabel: string;
  totalWeight: (n: number | string) => string;
  numberLabel: string;
  weightLabel: string;
  removeTitle: string;
  addEntry: string;
  resetDefaults: string;
  drawIdleStatus: string;
  drawStart: string;
  drawing: string;
  drawAgain: string;
  drawingStatus: string;
  resultStatus: (v: string) => string;
  historyTitle: string;
  historyItem: (i: number, t: string) => string;
}

const LANGS: Record<string, LangData> = {
  tw: {
    label: '繁體中文',
    htmlLang: 'zh-TW',
    locale: 'zh-TW',
    title: '數字機率抽獎系統',
    subtitle: '依自訂機率隨機抽出號碼。',
    desc: '為每個號碼設定抽中的權重（機率），權重越高被抽中的機會越大。設定完成後點擊「開始抽獎」，畫面上的數字會快速變動後停在最終結果。',
    sectionLabel: '號碼與機率設定',
    totalWeight: (n) => `總權重：${n}`,
    numberLabel: '號碼',
    weightLabel: '權重',
    removeTitle: '刪除此號碼',
    addEntry: '新增號碼',
    resetDefaults: '重設為預設值',
    drawIdleStatus: '設定好號碼與機率後即可開始抽獎',
    drawStart: '開始抽獎',
    drawing: '抽獎中…',
    drawAgain: '再抽一次',
    drawingStatus: '正在抽出號碼…',
    resultStatus: (v) => `本次抽出：${v}`,
    historyTitle: '抽獎紀錄',
    historyItem: (i, t) => `第 ${i} 次・${t}`,
  },
  cn: {
    label: '简体中文',
    htmlLang: 'zh-CN',
    locale: 'zh-CN',
    title: '数字概率抽奖系统',
    subtitle: '按自定义概率随机抽取号码。',
    desc: '为每个号码设置抽中的权重（概率），权重越高被抽中的机会越大。设置完成后点击「开始抽奖」，画面上的数字会快速变动后停在最终结果。',
    sectionLabel: '号码与概率设置',
    totalWeight: (n) => `总权重：${n}`,
    numberLabel: '号码',
    weightLabel: '权重',
    removeTitle: '删除此号码',
    addEntry: '新增号码',
    resetDefaults: '重置为默认值',
    drawIdleStatus: '设置好号码与权重后即可开始抽奖',
    drawStart: '开始抽奖',
    drawing: '抽奖中…',
    drawAgain: '再抽一次',
    drawingStatus: '正在抽取号码…',
    resultStatus: (v) => `本次抽出：${v}`,
    historyTitle: '抽奖记录',
    historyItem: (i, t) => `第 ${i} 次・${t}`,
  },
  en: {
    label: 'English',
    htmlLang: 'en',
    locale: 'en-US',
    title: 'Number Probability Lottery',
    subtitle: 'Randomly draw a number based on custom probabilities.',
    desc: 'Set a weight (probability) for each number — higher weight means a higher chance of being drawn. Once set, click "Start Draw" and the display will rapidly change before landing on the final result.',
    sectionLabel: 'Numbers & Probability Settings',
    totalWeight: (n) => `Total weight: ${n}`,
    numberLabel: 'Number',
    weightLabel: 'Weight',
    removeTitle: 'Remove this number',
    addEntry: 'Add Number',
    resetDefaults: 'Reset to Defaults',
    drawIdleStatus: 'Set up your numbers and weights, then start the draw',
    drawStart: 'Start Draw',
    drawing: 'Drawing…',
    drawAgain: 'Draw Again',
    drawingStatus: 'Drawing a number…',
    resultStatus: (v) => `Result: ${v}`,
    historyTitle: 'Draw History',
    historyItem: (i, t) => `Draw ${i} · ${t}`,
  },
  ja: {
    label: '日本語',
    htmlLang: 'ja',
    locale: 'ja-JP',
    title: '数字確率抽選システム',
    subtitle: 'カスタム確率に基づいて番号をランダムに抽選します。',
    desc: '各番号に当選する重み（確率）を設定してください。重みが大きいほど当選しやすくなります。設定後「抽選開始」を押すと、数字が素早く変化し、最終結果で停止します。',
    sectionLabel: '番号と確率の設定',
    totalWeight: (n) => `合計ウェイト：${n}`,
    numberLabel: '番号',
    weightLabel: 'ウェイト',
    removeTitle: 'この番号を削除',
    addEntry: '番号を追加',
    resetDefaults: 'デフォルトに戻す',
    drawIdleStatus: '番号とウェイトを設定すると抽選を開始できます',
    drawStart: '抽選開始',
    drawing: '抽選中…',
    drawAgain: 'もう一度抽選',
    drawingStatus: '番号を抽選しています…',
    resultStatus: (v) => `抽選結果：${v}`,
    historyTitle: '抽選履歴',
    historyItem: (i, t) => `${i} 回目・${t}`,
  },
  ko: {
    label: '한국어',
    htmlLang: 'ko',
    locale: 'ko-KR',
    title: '숫자 확률 추첨 시스템',
    subtitle: '사용자 지정 확률에 따라 번호를 무작위로 추첨합니다.',
    desc: '각 번호에 당첨 가중치(확률)를 설정하세요. 가중치가 높을수록 당첨될 확률이 높아집니다. 설정 후 "추첨 시작"을 누르면 숫자가 빠르게 바뀌다가 최종 결과에 멈춥니다.',
    sectionLabel: '번호 및 확률 설정',
    totalWeight: (n) => `총 가중치: ${n}`,
    numberLabel: '번호',
    weightLabel: '가중치',
    removeTitle: '이 번호 삭제',
    addEntry: '번호 추가',
    resetDefaults: '기본값으로 재설정',
    drawIdleStatus: '번호와 가중치를 설정하면 추첨을 시작할 수 있습니다',
    drawStart: '추첨 시작',
    drawing: '추첨 중…',
    drawAgain: '다시 추첨',
    drawingStatus: '번호를 추첨하는 중…',
    resultStatus: (v) => `추첨 결과: ${v}`,
    historyTitle: '추첨 기록',
    historyItem: (i, t) => `${i}회차・${t}`,
  },
};

const DEFAULT_ENTRIES: [string, number][] = [
  ['1', 10],
  ['2', 10],
  ['3', 10],
  ['4', 10],
  ['5', 10],
  ['6', 5],
];

function makeDefaultEntries(): LotteryEntry[] {
  let id = 0;
  return DEFAULT_ENTRIES.map(([n, w]) => ({ id: id++, number: n, weight: w }));
}

export function useLottery() {
  const [currentLang, setCurrentLang] = useState('tw');
  const [entries, setEntries] = useState<LotteryEntry[]>(makeDefaultEntries);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawDisplay, setDrawDisplay] = useState('???');
  const [drawStatus, setDrawStatus] = useState('');
  const [drawResult, setDrawResult] = useState('');
  const [drawPhase, setDrawPhase] = useState<'idle' | 'rolling' | 'landed'>('idle');
  const nextIdRef = useRef(DEFAULT_ENTRIES.length);

  const t = LANGS[currentLang];

  const addEntry = useCallback((number?: string, weight?: number) => {
    const nextNumber = number !== undefined ? number : String(entries.length + 1);
    const w = weight !== undefined ? weight : 10;
    const id = nextIdRef.current++;
    setEntries((prev) => [...prev, { id, number: nextNumber, weight: w }]);
  }, [entries.length]);

  const removeEntry = useCallback((id: number) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const updateEntry = useCallback((id: number, field: 'number' | 'weight', value: string) => {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        if (field === 'weight') {
          const num = parseFloat(value);
          return { ...e, weight: isNaN(num) ? 0 : num };
        }
        return { ...e, number: value };
      })
    );
  }, []);

  const resetDefaults = useCallback(() => {
    nextIdRef.current = 0;
    const newEntries: LotteryEntry[] = DEFAULT_ENTRIES.map(([n, w]) => ({
      id: nextIdRef.current++,
      number: n,
      weight: w,
    }));
    setEntries(newEntries);
  }, []);

  const validEntries = entries.filter(
    (e) => e.number.trim() !== '' && e.weight > 0
  );

  const totalWeight = validEntries.reduce((s, e) => s + e.weight, 0);

  const getProbability = useCallback(
    (entry: LotteryEntry) => {
      if (totalWeight <= 0 || entry.weight <= 0) return 0;
      return (entry.weight / totalWeight) * 100;
    },
    [totalWeight]
  );

  const weightedPick = useCallback(() => {
    if (validEntries.length === 0) return '';
    const total = validEntries.reduce((s, e) => s + e.weight, 0);
    let r = Math.random() * total;
    for (const e of validEntries) {
      if (r < e.weight) return e.number;
      r -= e.weight;
    }
    return validEntries[validEntries.length - 1].number;
  }, [validEntries]);

  const startDraw = useCallback(async (onTick?: (value: string, progress: number) => void) => {
    if (isDrawing || validEntries.length === 0) return;

    setIsDrawing(true);
    setDrawPhase('rolling');
    setDrawStatus(t.drawingStatus);

    const pool = validEntries.map((e) => e.number);
    const winner = weightedPick();

    const totalDuration = 1400;
    const start = performance.now();

    await new Promise<void>((resolve) => {
      function tick() {
        const elapsed = performance.now() - start;
        const progress = Math.min(elapsed / totalDuration, 1);
        const randomVal = pool[Math.floor(Math.random() * pool.length)];
        setDrawDisplay(randomVal);
        if (onTick) {
          onTick(randomVal, progress);
        }
        const delay = 40 + Math.pow(progress, 3) * 260;
        if (progress < 1) {
          setTimeout(tick, delay);
        } else {
          resolve();
        }
      }
      tick();
    });

    setDrawDisplay(winner);
    setDrawPhase('landed');
    setDrawResult(winner);
    setDrawStatus(t.resultStatus(winner));

    setHistory((prev) => [...prev, { number: winner, time: new Date() }]);

    setIsDrawing(false);
  }, [isDrawing, validEntries, weightedPick, t]);

  const changeLang = useCallback((code: string) => {
    if (!LANGS[code]) return;
    setCurrentLang(code);
    document.documentElement.lang = LANGS[code].htmlLang;
    if (!isDrawing) {
      setDrawStatus(LANGS[code].drawIdleStatus);
    }
  }, [isDrawing]);

  return {
    t,
    currentLang,
    entries,
    history,
    isDrawing,
    drawDisplay,
    drawStatus,
    drawResult,
    drawPhase,
    totalWeight,
    validEntries,
    addEntry,
    removeEntry,
    updateEntry,
    resetDefaults,
    startDraw,
    changeLang,
    getProbability,
    langList: Object.keys(LANGS),
  };
}